import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import {
  Payment,
  PaymentService,
  CreatePaymentRequest,
  UpdatePaymentRequest
} from '../../../core/services/payment.service';
import { Member, MemberService } from '../../../core/services/member.service';
import { TenantContextService } from '../../../core/services/tenant-context.service';
import { BusinessUnitStore } from '../../../core/stores/business-unit.store';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { CustomFieldsInputComponent } from '../../../shared/components/custom-fields-input/custom-fields-input.component';

@Component({
  selector: 'app-payment-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SpinnerComponent, CustomFieldsInputComponent],
  templateUrl: './payment-form.component.html'
})
export class PaymentFormComponent implements OnChanges, OnDestroy {
  private fb = inject(FormBuilder);
  private paymentService = inject(PaymentService);
  private memberService = inject(MemberService);
  private tenantContext = inject(TenantContextService);
  private buStore = inject(BusinessUnitStore);

  @Input() open = false;
  @Input() payment: Payment | null = null;
  @Input() initialMember: Member | null = null;
  @Input() initialMonth: number | null = null;
  @Input() initialYear: number | null = null;
  /** Explicit prefill amount. Takes precedence over initialMember.monthlyFee so callers
   *  can pass a computed shortfall (expected − totalPaid) for top-up flows. */
  @Input() initialAmount: number | null = null;
  @Input() lockMember = false;

  @ViewChild('cf') cf?: CustomFieldsInputComponent;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<Payment>();

  isSubmitting = false;
  errorMessage = '';

  // Member picker
  memberSearch = '';
  memberResults: Member[] = [];
  selectedMember: Member | null = null;
  private memberSearch$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  months = [
    { value: 1, name: 'January' },   { value: 2, name: 'February' },
    { value: 3, name: 'March' },     { value: 4, name: 'April' },
    { value: 5, name: 'May' },       { value: 6, name: 'June' },
    { value: 7, name: 'July' },      { value: 8, name: 'August' },
    { value: 9, name: 'September' }, { value: 10, name: 'October' },
    { value: 11, name: 'November' }, { value: 12, name: 'December' }
  ];

  years: number[] = [];

  readonly form: FormGroup = this.fb.group({
    memberId:      [null, [Validators.required]],
    month:         [new Date().getMonth() + 1, [Validators.required]],
    year:          [new Date().getFullYear(), [Validators.required]],
    amount:        [null, [Validators.required, Validators.min(0.01)]],
    paymentMethod: ['Cash', [Validators.required]],
    reference:     ['']
  });

  get f() { return this.form.controls; }
  get isEditMode(): boolean { return !!this.payment; }
  get title(): string { return this.isEditMode ? 'Edit Payment' : 'Record Payment'; }
  get customFieldsBuId(): string | null { return this.tenantContext.getCurrentBusinessUnitId(); }

  get selectedBuName(): string {
    const buId = this.tenantContext.getCurrentBusinessUnitId();
    if (buId == null) return 'No committee selected';
    const unit = this.buStore.units().find((u: any) => u.id === buId || u.businessUnitId === buId);
    return unit?.name || `Committee #${buId}`;
  }

  constructor() {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 1; i++) this.years.push(i);

    this.memberSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(q => this.searchMembers(q));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) this.resetForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onMemberSearchInput(): void { this.memberSearch$.next(this.memberSearch); }

  private searchMembers(query: string): void {
    if (!query || query.length < 1) { this.memberResults = []; return; }
    this.memberService.searchMembers(query, 10).subscribe({
      next: (res) => {
        if (res.success && res.data) this.memberResults = res.data as Member[];
      }
    });
  }

  selectMember(m: Member): void {
    this.selectedMember = m;
    this.memberResults = [];
    this.memberSearch = m.fullName;
    this.form.patchValue({ memberId: m.id });
    if (m.monthlyFee != null && this.form.value.amount == null) {
      this.form.patchValue({ amount: m.monthlyFee });
    }
  }

  clearMember(): void {
    this.selectedMember = null;
    this.memberSearch = '';
    this.form.patchValue({ memberId: null });
  }

  close(): void { if (!this.isSubmitting) this.closed.emit(); }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorMessage = 'Please fix the highlighted fields.';
      return;
    }

    if (!this.isEditMode && this.tenantContext.getCurrentBusinessUnitId() == null) {
      this.errorMessage = 'Select a committee from the top bar before recording a payment.';
      return;
    }

    if (!this.isEditMode && this.cf && !this.cf.valid) {
      this.cf.touch();
      this.errorMessage = 'Please complete the required additional fields.';
      return;
    }

    const v = this.form.value;
    this.isSubmitting = true;
    this.errorMessage = '';

    if (this.isEditMode && this.payment?.id != null) {
      const req: UpdatePaymentRequest = {
        amount: Number(v.amount),
        paymentMethod: v.paymentMethod,
        reference: v.reference?.trim() || null
      };
      this.paymentService.updatePayment(this.payment.id, req).subscribe({
        next: (res) => {
          this.isSubmitting = false;
          if (res.success && res.data) {
            this.saved.emit(res.data);
          } else {
            this.errorMessage = res.message || 'Update failed.';
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.errorMessage = err?.error?.message || 'Update failed.';
        }
      });
    } else {
      const req: CreatePaymentRequest = {
        memberId: v.memberId,
        month: Number(v.month),
        year: Number(v.year),
        amount: Number(v.amount),
        paymentMethod: v.paymentMethod,
        reference: v.reference?.trim() || null,
        customFields: this.cf?.getValues() ?? {}
      };
      this.paymentService.createPayment(req).subscribe({
        next: (res) => {
          this.isSubmitting = false;
          if (res.success && res.data) {
            this.saved.emit(res.data);
          } else {
            this.errorMessage = res.message || 'Create failed.';
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.errorMessage = err?.error?.message || 'Create failed.';
        }
      });
    }
  }

  private resetForm(): void {
    this.errorMessage = '';
    this.memberResults = [];
    if (this.payment) {
      this.selectedMember = null;
      this.memberSearch = this.payment.memberName || `Member #${this.payment.memberId}`;
      this.form.reset({
        memberId:      this.payment.memberId,
        month:         this.payment.month,
        year:          this.payment.year,
        amount:        this.payment.amount,
        paymentMethod: this.payment.paymentMethod,
        reference:     this.payment.reference || ''
      });
    } else {
      this.selectedMember = null;
      this.memberSearch = '';
      const now = new Date();
      const month = this.initialMonth ?? (now.getMonth() + 1);
      const year = this.initialYear ?? now.getFullYear();
      const memberId = this.initialMember?.id ?? null;
      // initialAmount wins (used for top-up shortfall); fall back to the member's monthly fee.
      const amount = this.initialAmount ?? this.initialMember?.monthlyFee ?? null;
      this.form.reset({
        memberId,
        month,
        year,
        amount,
        paymentMethod: 'Cash',
        reference:     ''
      });
      if (this.initialMember) {
        this.selectedMember = this.initialMember;
        this.memberSearch = this.initialMember.fullName;
      }
    }
  }
}
